import {Advance, Record} from '../model/broadcast/player';

/**
 * The diamond a scoresheet cell draws, built from one plate appearance and the
 * legs the runner ran afterwards.
 *
 * A paper cell holds two things: the play notation, and a diamond showing what
 * became of the man. The diamond is annotated by corner —
 *
 *   - a corner reached because a **batter** did something (a hit, or a walk or
 *     hit-by-pitch that forced him up) carries that batter's batting-order
 *     number;
 *   - a corner reached on the runner's own account (a steal, a wild pitch, a
 *     passed ball, a balk, an error on the bases) carries the code for it;
 *   - a runner retired is written in the **middle**;
 *   - a runner who scored gets the diamond filled in.
 *
 * `Advance.by` is what separates the first two: absent means the batter of
 * `Advance.seq` moved him, so the corner shows that batter's number. The server
 * gets that distinction free — WBSC writes a batter-caused advance as a bare
 * clause and names the reason for everything else (see its base-running.ts).
 *
 * The batter's own arrival needs no annotation: the play code in the middle of
 * the cell already says how he got on.
 */

/** Bases in scoresheet order: 1st right, 2nd top, 3rd left, home bottom. */
export type Base = 1 | 2 | 3 | 4;

export interface DiamondCorner {
  base: Base;
  /** A batting-order number, or a code like `SB` / `CS2-6`. */
  label: string;
  /** He was retired attempting this leg, so the corner reads as an out. */
  out?: boolean;
}

export interface Diamond {
  /** Base the plate appearance itself put him on: 1-3, 4 on a home run. */
  reached: number;
  /** Farthest base he stood on, so the path can be drawn leg by leg. */
  farthest: number;
  /** He came home. The diamond is filled. */
  scored: boolean;
  /**
   * Retired at FIRST, which is the one out with no corner to live in — the
   * bottom-right belongs to how he reached it. Every other out is a corner,
   * because it is a leg he attempted and failed.
   */
  out?: { base: Base; label: string };
  /** Annotations, at most one per corner, in base order. */
  corners: DiamondCorner[];
  /** Runs this plate appearance drove in, drawn as dots. */
  rbi: number;
}

/**
 * Which batting-order slot each plate appearance belongs to, so a bare leg can
 * be labelled with the number of the batter who caused it.
 *
 * Built from the team's own lineup: only its own batters move its own runners,
 * so no cross-team lookup is ever needed. Spots are 1-based, the way a scorer
 * numbers them.
 */
export function battingOrderBySeq(lineup: { players?: { records?: Record[] }[] }[] | undefined): Map<number, number> {
  const bySeq = new Map<number, number>();
  (lineup ?? []).forEach((spot, index) => {
    (spot.players ?? []).forEach(player => {
      (player.records ?? []).forEach(record => {
        if (record.seq !== undefined) bySeq.set(record.seq, index + 1);
      });
    });
  });
  return bySeq;
}

/**
 * The diamond for one cell, or null when the server said nothing about the
 * bases — a game parsed before it wrote `reached`.
 *
 * A plate appearance that went nowhere still gets a diamond, because a paper
 * sheet has one printed in every cell: what changes is how much of it is
 * inked. A strikeout is an empty diamond, not an absent one.
 */
export function diamondFor(record: Record, orderBySeq: Map<number, number>): Diamond | null {
  const legs = record.advances ?? [];
  if (record.reached === undefined) return null;

  const diamond: Diamond = {
    reached: record.reached,
    farthest: record.reached,
    scored: record.reached === 4,
    corners: [],
    rbi: record.rbi ?? 0,
  };

  legs.forEach(leg => {
    if (leg.out) {
      // An out is written where the leg would have ended: a man caught stealing
      // second has CS2-6 in the top-right corner, the same corner a successful
      // steal would have used. Only an out at first has nowhere to go, because
      // that corner says how he got on.
      const at = asBase(leg.to);
      const credit = fieldingCredit(leg.by ?? '');
      if (at === 1) diamond.out = {base: at, label: credit};
      else diamond.corners.push({base: at, label: credit || '\u00d7', out: true});
      return;
    }
    if (leg.to > diamond.farthest) diamond.farthest = leg.to;
    if (leg.to === 4) diamond.scored = true;
    diamond.corners.push({base: asBase(leg.to), label: cornerLabel(leg, orderBySeq)});
  });

  diamond.corners.sort((a, b) => a.base - b.base);
  return diamond;
}

/**
 * What goes in the corner: the code when the runner earned the base himself,
 * and otherwise the number of the batter who moved him.
 *
 * A batter whose own plate appearance is not in this lineup cannot happen — a
 * team's runners are only ever moved by its own batters — but an old document
 * carries no `seq`, so the fallback is a bullet rather than an empty corner
 * that would read as "nothing happened here".
 */
function cornerLabel(leg: Advance, orderBySeq: Map<number, number>): string {
  if (leg.by) return leg.by;
  const spot = orderBySeq.get(leg.seq);
  return spot ? String(spot) : '•';
}

/**
 * A fielding credit as a scorer writes it: the digits are the men who handled
 * the ball, so they are separated the way the play code separates them —
 * `CS26` becomes `CS2-6`, `PO25` becomes `PO2-5`, a bare `64` becomes `6-4`.
 *
 * A single digit is left alone (`4`), and so is anything that is not a run of
 * digits at the end: `E5` is one error by one fielder, and `E1T` carries a
 * trailing T for the throw.
 */
export function fieldingCredit(code: string): string {
  const match = /^([A-Z]*)(\d{2,})$/.exec(code);
  if (!match) return code;
  return match[1] + match[2].split('').join('-');
}

function asBase(base: number): Base {
  return (base < 1 ? 1 : base > 4 ? 4 : base) as Base;
}

/** Whether this leg of the path was travelled, so the edge is drawn. */
export function legTravelled(diamond: Diamond, base: Base): boolean {
  return diamond.farthest >= base;
}
